import { RouteProp, useRoute } from "@react-navigation/native";
import { useNavigation } from "@react-navigation/native";
import React, { useCallback, useRef, useState } from "react";
import { FlatList, Pressable, ScrollView, StyleSheet, TextInput, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { Modal } from "../components";
import { kColors } from "../kColors";
import { kRoute, tRouteParams } from "../kRoute";
import { kStyleConst } from "../kStyle";
import { createDraft, createSingleDraft, useFetchDraftFromId } from "../redux/draftCart/thunk";
import BrandCart, { BrandCartItem } from "../components/BrandCart";
import OrderSummary from "../components/OrderSummary";
import { PdPencil } from "src/components/Icons";
import { copyLinkToClipboard } from "src/util/functions";
import { useIsMobileAndTablet, useUserProfile } from "src/hooks/ui";
import { useAppDispatch } from "src/redux";
import { tDetailedCart } from "tmt_api/src/types/types.validator";
import _ from "lodash";
import { fonts } from "src/fonts";
import { sortBySku } from "src/util/cart";
import { formatMoney } from "src/util/gFormatting";
import { PdStack, PdText } from "src/components/layout";
import { PdFormLabel } from "src/components/form-control";
import { isApiError } from "src/util/apiNew";
import { addPromoCode } from "src/redux/user/actions";
import { PdInput } from "src/components/forms/input";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { useConfirmModal } from "src/hooks/modals";

const DEFAULT_TITLE = "Suggested Order For You!";
const DEFAULT_DESCRIPTION = "I put this together for you; it includes our top sellers!";

const EmptyDraftCarts: React.FC = () => {
  const navigation = useNavigation();

  const onPress = () => {
    navigation.navigate(kRoute.P99);
  };

  return (
    <View style={[styles.flexRow, styles.justifyCenter, styles.flex1, styles.my10]}>
      <TouchableOpacity onPress={onPress}>
        <PdText style={[styles.textItalic, styles.textUnderline]}>Shop</PdText>
      </TouchableOpacity>
      <PdText style={styles.textItalic}> your brands to create orders and share them with your retailers!</PdText>
    </View>
  );
};

const BrandDraftScreen: React.FC = () => {
  const route = useRoute<RouteProp<tRouteParams, typeof kRoute.P103>>();
  const titleInputRef = useRef<TextInput>(null);
  const isMobileTablet = useIsMobileAndTablet();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  useFetchDraftFromId(route.params?.draftId ?? "", true);

  const rCart = useSelector(({ UserReducer }) => UserReducer.cart);
  const [sDraftLink, setDraftLink] = useState("Send Draft Cart");

  const [sShowSuccessModal, setShowModal] = useState(false);
  const [sTitle, setTitle] = useState("");
  const [sDescription, setDescription] = useState("");

  const _cartKeyExtractor = useCallback((myCartItem) => myCartItem.name, []);

  const _cartRenderMethod = useCallback(({ item }) => <BrandCart draftPage={true} item={item} />, []);

  const handleOnPressModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleCopyNow = useCallback(() => {
    copyLinkToClipboard(sDraftLink, sTitle);
    setShowModal(false);
    dispatch({ type: "RIBBON", message: "Link copied to clipboard" });
    navigation.navigate(kRoute.P04);
  }, [dispatch, navigation, sDraftLink, sTitle]);

  const handleSubmitDraft = async () => {
    const link = await dispatch(createDraft(sDescription, sTitle));
    if (link) setDraftLink(link);
    setShowModal(true);
  };

  return (
    <>
      {sShowSuccessModal && (
        <Modal
          onCancel={handleOnPressModal}
          title="Successfully Created a Draft Order"
          body={{
            content:
              "Copy the link and share it with your retailers by messaging or emailing them. This order will be saved in your 'Drafts Order' section.",
          }}
          buttons={[
            {
              id: "draft-copy-modal-cancel",
              label: "Maybe Later",
              onPress: handleOnPressModal,
              isPrimaryAction: false,
            },
            {
              id: "draft-copy-modal-confirm",
              label: "Copy Now",
              onPress: handleCopyNow,
              isPrimaryAction: true,
            },
          ]}
        />
      )}
      <ScrollView
        maintainVisibleContentPosition={{ minIndexForVisible: 1 }}
        style={styles.checkoutScrollView}
        contentContainerStyle={[
          styles.checkOutContainer,
          isMobileTablet ? [styles.my2, styles.mx4] : [styles.flexRow, styles.my10, styles.mx14],
        ]}
      >
        {/* SHOPPING CART SECTION */}
        <View style={styles.checkoutProductContainer}>
          <PdFormLabel>Title</PdFormLabel>
          <View style={[styles.flexRow, styles.alignCenter, styles.mb5]}>
            <TextInput
              ref={titleInputRef}
              onChangeText={setTitle}
              style={[styles.inputTitle, isMobileTablet ? styles.widthFull : styles.widthMd]}
              placeholder={rCart ? rCart[0]?.draftTitle || DEFAULT_TITLE : DEFAULT_TITLE}
              placeholderTextColor={kColors.mediumGrey}
            />
            <Pressable style={styles.inputTitleIconContainer} onPress={() => titleInputRef.current?.focus()}>
              <PdPencil style={[styles.inputTitleIcon, styles.ml3]} color={kColors.brandDark} />
            </Pressable>
          </View>
          <PdFormLabel>Description</PdFormLabel>
          <TextInput
            multiline={true}
            onChangeText={setDescription}
            style={[styles.inputDescription, styles.mb5]}
            placeholder={rCart ? rCart[0]?.draftDescription || DEFAULT_DESCRIPTION : DEFAULT_DESCRIPTION}
            placeholderTextColor={kColors.mediumGrey}
          />
          <PdFormLabel>Suggested Purchases for Retailers</PdFormLabel>
          <FlatList
            data={rCart}
            keyExtractor={_cartKeyExtractor}
            renderItem={_cartRenderMethod}
            scrollEnabled={true}
            showsVerticalScrollIndicator={false}
          />
        </View>
        {/* END SHOPPING CART SECTION */}
        {/* ORDER SUMMARY SECTION */}
        <View style={isMobileTablet ? styles.mt3 : styles.ml10}>
          <PdFormLabel>Order Summary</PdFormLabel>
          <View style={styles.divider} />
          <OrderSummary onDraftSubmit={handleSubmitDraft} isDraftPage={true} />
        </View>
        {/* END ORDER SUMMARY SECTION */}
      </ScrollView>
    </>
  );
};

const DraftCart: React.FC<{ item: tDetailedCart }> = ({ item }) => {
  const dispatch = useAppDispatch();
  const isMobileTablet = useIsMobileAndTablet();
  const navigation = useNavigation();
  const confirmModal = useConfirmModal();
  const [promotion, setPromotion] = useState("");
  const [promotionError, setPromotionError] = useState("");
  const titleInputRef = React.useRef<TextInput>(null);
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");

  const handleNavigateToBrand = () => {
    navigation.navigate(kRoute.P100, { brand: item.products[0]?.brandName });
  };

  const handleOnChangeTextPromotion = (newPromotion: string) => {
    if (promotionError) setPromotionError("");
    setPromotion(newPromotion);
  };

  const handleOnBlurPromotion = async () => {
    const formattedPromotion = promotion.trim().toLowerCase();
    if (formattedPromotion && formattedPromotion !== item.discountCode) {
      try {
        await dispatch(addPromoCode(item.cartId, formattedPromotion));
      } catch (error) {
        if (isApiError(error)) {
          const message = error.response?.data?.message ?? "An error occurred please try again.";
          setPromotionError(message);
          dispatch({ type: "RIBBON", message: new Error(message) });
        }
      }
    }
  };

  const handleOnPressShareOrder = async () => {
    const link = await dispatch(createSingleDraft(item.cartId, title, description));

    if (!link) {
      dispatch({
        type: "RIBBON",
        message: new Error("It was not possible to create draft link. Please try again later."),
      });
      return;
    }

    const response = await confirmModal.openAsync({
      title: "Successfully Created a Draft Order",
      description:
        "Copy the link and share it with your retailers by messaging or emailing them. This order will be saved in your 'Drafts Order' section.",
      confirmButtonLabel: "Copy Now",
    });
    if (response.status === "closed") return;

    copyLinkToClipboard(link, title);
    dispatch({ type: "RIBBON", message: "Link copied to clipboard" });
    navigation.navigate(kRoute.P04);
  };

  return (
    <View style={[styles.draftCartItem, styles.p4, styles.mb4]}>
      <PdStack
        direction={isMobileTablet ? "vertical" : "horizontal"}
        isReversed={isMobileTablet}
        style={[isMobileTablet ? [] : styles.alignStart, styles.justifyBetween]}
      >
        <View>
          <PdFormLabel>Title</PdFormLabel>
          <View style={[styles.flexRow, styles.alignCenter, styles.mb5]}>
            <TextInput
              ref={titleInputRef}
              onChangeText={setTitle}
              style={[styles.inputTitle, isMobileTablet ? styles.widthFull : styles.widthMd]}
              placeholder={DEFAULT_TITLE}
              placeholderTextColor={kColors.mediumGrey}
            />
            <Pressable style={styles.inputTitleIconContainer} onPress={() => titleInputRef.current?.focus()}>
              <PdPencil style={[styles.inputTitleIcon, styles.ml3]} color={kColors.brandDark} />
            </Pressable>
          </View>
        </View>
        <PdButton onPress={handleOnPressShareOrder}>
          <PdButtonLabel>Share this Order</PdButtonLabel>
        </PdButton>
      </PdStack>
      <PdFormLabel>Description</PdFormLabel>
      <TextInput
        multiline={true}
        onChangeText={setDescription}
        style={[styles.inputDescription, styles.mb5]}
        placeholder={DEFAULT_DESCRIPTION}
        placeholderTextColor={kColors.mediumGrey}
      />
      <View style={[styles.flexRow, styles.alignCenter]}>
        <PdFormLabel>Suggested Purchases for Retailers</PdFormLabel>
        <TouchableOpacity style={styles.mlAuto} onPress={handleNavigateToBrand}>
          <PdText style={styles.brandCartTitle}>{item?.name}</PdText>
        </TouchableOpacity>
      </View>
      <PdStack direction="vertical" style={[styles.draftCartItem, styles.p4]}>
        <FlatList
          keyExtractor={(cartItem, index) => `${cartItem.actualSKU}  ${index}`}
          data={_.cloneDeep(item?.products).sort(sortBySku)}
          renderItem={({ item: cartProduct }) => <BrandCartItem item={cartProduct} />}
          maintainVisibleContentPosition={{ minIndexForVisible: 1 }}
          scrollEnabled={false}
        />
        <View>
          <PdFormLabel>Promotion</PdFormLabel>
          <View>
            <PdInput
              onChangeText={handleOnChangeTextPromotion}
              onBlur={handleOnBlurPromotion}
              value={promotion}
              style={promotionError ? styles.borderRed : {}}
              placeholderTextColor={kColors.mediumGrey}
              placeholder="Enter promotion code here"
            />
            {promotionError && <PdText style={fonts.ErrorSmall}>Invalid promotion code or under minimum</PdText>}
          </View>
        </View>
        <View style={styles.alignEnd}>
          <PdText style={[styles.textBlack, styles.pb2]}>
            Product Total: {formatMoney(item.totalPrice + (item?.discountCost ?? 0))}
          </PdText>
          {(item?.discountCost ?? 0) > 0 && (
            <PdText style={[styles.textBlack, styles.pb2]}>Discount: {formatMoney(item.discountCost ?? "0.00")}</PdText>
          )}
          <PdText style={styles.textBlack}>Total: {formatMoney(item.totalPrice + item.totalPriceWithShipping)}</PdText>
        </View>
      </PdStack>
    </View>
  );
};

const SalesRepScreen: React.FC = () => {
  const isMobileTablet = useIsMobileAndTablet();
  const rCarts = useSelector(({ UserReducer }) => UserReducer.cart);

  return (
    <FlatList
      style={styles.checkoutScrollView}
      contentContainerStyle={[styles.checkOutContainer, isMobileTablet ? [styles.m4] : [styles.m10]]}
      data={rCarts}
      keyExtractor={(item) => item.name}
      renderItem={({ item }) => <DraftCart item={item} />}
      scrollEnabled={true}
      showsVerticalScrollIndicator={false}
      ListEmptyComponent={EmptyDraftCarts}
    />
  );
};

const P103_DraftCartCheckout: React.FC = () => {
  const { isBrand, isRep } = useUserProfile();

  if (isBrand) {
    return <BrandDraftScreen />;
  }

  if (isRep) {
    return <SalesRepScreen />;
  }

  return null;
};

export const P103 = React.memo(P103_DraftCartCheckout);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  alignEnd: {
    alignItems: "flex-end",
  },
  alignStart: {
    alignItems: "flex-start",
  },
  borderRed: {
    borderColor: kColors.red,
  },
  brandCartTitle: {
    color: kColors.brand500,
    fontFamily: "Raleway",
    fontSize: 12,
    fontWeight: "700",
    textTransform: "uppercase",
  },
  checkOutContainer: {
    paddingBottom: 10 * kStyleConst.kSpacing,
  },
  checkoutProductContainer: {
    alignItems: "stretch",
    flex: 1,
    flexDirection: "column",
    minWidth: 300,
  },
  checkoutScrollView: {
    alignContent: "center",
  },
  divider: {
    backgroundColor: kColors.gray300,
    height: 1,
    width: "100%",
  },
  draftCartItem: {
    borderColor: kColors.gray300,
    borderWidth: 1,
    flexDirection: "column",
    flex: 1,
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  inputDescription: {
    borderColor: kColors.gray300,
    borderWidth: 0.75,
    color: kColors.brand500,
    fontFamily: "Poppins",
    fontSize: 14,
    fontWeight: "300",
    height: 68,
    letterSpacing: 1.1,
    outlineColor: kColors.transparent,
    outlineWidth: 0,
    padding: 3 * kStyleConst.kSpacing,
  },
  inputTitle: {
    borderColor: kColors.gray300,
    borderWidth: 0.75,
    color: kColors.brand500,
    fontFamily: "Poppins",
    fontSize: 22,
    height: 50,
    letterSpacing: 1.1,
    outlineColor: kColors.transparent,
    padding: 3 * kStyleConst.kSpacing,
    zIndex: 10,
  },
  inputTitleIcon: {
    height: 10,
    width: 10,
  },
  inputTitleIconContainer: {
    alignItems: "center",
    height: "100%",
    justifyContent: "center",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  m10: {
    margin: 10 * kStyleConst.kSpacing,
  },
  m4: {
    margin: 4 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: 5 * kStyleConst.kSpacing,
  },
  ml10: {
    marginLeft: 10 * kStyleConst.kSpacing,
  },
  ml3: {
    marginLeft: 3 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mt3: {
    marginTop: 3 * kStyleConst.kSpacing,
  },
  mx14: {
    marginHorizontal: 14 * kStyleConst.kSpacing,
  },
  mx4: {
    marginHorizontal: 4 * kStyleConst.kSpacing,
  },
  my10: {
    marginVertical: 10 * kStyleConst.kSpacing,
  },
  my2: {
    marginVertical: 2 * kStyleConst.kSpacing,
  },
  p4: {
    padding: 4 * kStyleConst.kSpacing,
  },
  pb2: {
    paddingBottom: 2 * kStyleConst.kSpacing,
  },
  textBlack: {
    color: kColors.black,
  },
  textItalic: {
    fontStyle: "italic",
  },
  textUnderline: {
    textDecorationLine: "underline",
  },
  widthFull: {
    width: "100%",
  },
  widthMd: {
    width: 340,
  },
});
